import { useSelector } from '../../../../hooks/useSelector'
import { useMemo, useCallback } from 'react'
import { PrivateTrade } from '@prometheus/types'
import usePlayerActions from '../../hooks/usePlayerActions'
import { translateResource, translateResourceColorRaw } from '../../../../utils/translateResource'
import formatCost from '../../../../utils/formatCost'
import classNames from 'classnames'
import { Box, Card, Cards, Divider, Typography } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'

interface PrivateTradeOfferProps {
  offer: PrivateTrade
}

const PrivateTradeOffer = ({ offer }: PrivateTradeOfferProps) => {
  const players = useSelector(s => s.game.metadata?.players) || {}
  const uid = useSelector(s => s.auth.uid) || ''
  const { onOfferTrade } = usePlayerActions()

  const onRevoke = useCallback(() => {
    return onOfferTrade('revoke', offer)
  }, [onOfferTrade, offer])

  const onAccept = useCallback(() => {
    return onOfferTrade('accept', offer)
  }, [onOfferTrade, offer])

  const targetDescription = offer.from === uid ? 'To' : 'From'
  const target = offer.from === uid ? players[offer.to]?.name : players[offer.from]?.name
  const color = offer.from === uid ? players[offer.to]?.color : players[offer.from]?.color

  const isOwner = offer.from === uid

  return (
    <Card padding="sm">
      <div style={{ position: 'relative' }}>
        <Typography variant="h4">
          {targetDescription}: <span style={{ color }}>{target}</span>
        </Typography>
        {isOwner && (
          <PromiseButtonV2 onClick={onRevoke} size="xs" style={{ position: 'absolute', top: 0, right: 0, marginRight: 0 }} variant="danger" outline>Revoke</PromiseButtonV2>
        )}
        <Typography variant="h6">Offering <span style={{ color: translateResourceColorRaw(offer.resource) }}><strong style={{ color: 'var(--prometheus-primary)' }}>{offer.quantity}</strong> {translateResource(offer.resource)}</span>. Asking: <span style={{ color: 'var(--prometheus-primary)' }}>{formatCost(offer.cost)}</span></Typography>
        {!isOwner && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--prometheus-spacing-sm)', marginTop: 'var(--prometheus-spacing-sm)' }}>
            <PromiseButtonV2 ignoreGameLogic onClick={onRevoke} size="sm" variant="danger" outline>Decline</PromiseButtonV2>
            <PromiseButtonV2 ignoreGameLogic onClick={onAccept} size="sm" variant="primary">Accept</PromiseButtonV2>
          </div>
        )}
      </div>
    </Card>
  )
}

export const PrivateTradeOffers = () => {
  const _offers = useSelector(s => s.game.metadata?.private_trade) || []
  const uid = useSelector(s => s.auth.uid) || ''
  const is_map = useSelector(s => s.game.galaxy_display)

  const offers = useMemo(() => _offers.filter((trade) => [trade.from, trade.to].indexOf(uid) !== -1), [_offers, uid])

  if (!offers.length) {
    return null
  }

  return (
    <Card transparent padding="none" className={classNames({ is_map })} style={{ zIndex: 1, position: 'relative', borderLeft: 'none', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
      <Box space="sm">
        <Typography retro variant="h4" color="primary">PRIVATE TRADE OFFERS</Typography>
      </Box>
      <Divider />
      <Box space="sm">
        <Cards>
          {offers.map((offer) => <PrivateTradeOffer key={offer.id} offer={offer} />)}
        </Cards>
      </Box>
    </Card>
  )
}
